/**
 * Focus Indicator Utilities - WCAG 2.4.7 Compliance
 *
 * Centralized focus indicator styles for consistent accessibility across the app.
 * Import these utilities in components instead of writing focus styles inline.
 *
 * @see https://www.w3.org/WAI/WCAG21/Understanding/focus-visible.html
 *
 * Usage:
 *   import { focusIndicatorStyles } from 'lib/styles/focusIndicator'
 *
 *   const buttonCss = css`
 *     ${focusIndicatorStyles}
 *   `
 */
import { css } from '@emotion/react'
import { baseColor } from './colors'

// Default focus color - accessible blue that meets 3:1 contrast
const defaultFocusColor = '#0066cc'

/**
 * Standard focus indicator for most interactive elements.
 * Uses :focus-visible to only show on keyboard navigation (not mouse clicks).
 *
 * Use for: buttons, links, inputs, and most interactive elements
 */
export const focusIndicatorStyles = css`
  &:focus-visible {
    outline: 2px solid ${defaultFocusColor};
    outline-offset: 2px;
    z-index: 1; /* Ensure focus outline appears above adjacent elements */
  }
`

/**
 * Focus indicator using the app's baseColor (primary brand color).
 * Use when the focus ring should match the app's primary color scheme.
 *
 * Use for: elements where brand consistency is important
 */
export const focusIndicatorBaseColor = css`
  &:focus-visible {
    outline: 2px solid ${baseColor};
    outline-offset: 2px;
    z-index: 1; /* Ensure focus outline appears above adjacent elements */
  }
`

/**
 * Inset focus indicator for table rows and card-like elements.
 * Uses box-shadow instead of outline to stay within element bounds.
 *
 * Use for: table rows, list items, cards with hover states
 *
 * @param color - Focus ring color (default: #0066cc)
 * @param width - Shadow width (default: 2px)
 */
export const insetFocusIndicator = (color = defaultFocusColor, width = '2px') => css`
  &:focus-visible {
    box-shadow: inset 0 0 0 ${width} ${color};
    outline: none;
  }
`

/**
 * Inset focus indicator using baseColor.
 * Combines inset style with the app's primary color.
 */
export const insetFocusIndicatorBaseColor = css`
  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${baseColor};
    outline: none;
  }
`

/**
 * Customizable focus indicator for elements needing different colors/sizes.
 *
 * @param color - Focus ring color (default: #0066cc)
 * @param width - Outline width (default: 2px)
 * @param offset - Outline offset from element (default: 2px)
 */
export const customFocusIndicator = (
  color = defaultFocusColor,
  width = '2px',
  offset = '2px'
) => css`
  &:focus-visible {
    outline: ${width} solid ${color};
    outline-offset: ${offset};
    z-index: 1; /* Ensure focus outline appears above adjacent elements */
  }
`

/**
 * Focus indicator with negative offset (tighter fit).
 * Useful for elements where the outline should appear inside the border.
 *
 * Use for: buttons with borders, compact UI elements
 */
export const focusIndicatorInline = css`
  &:focus-visible {
    outline: 2px solid ${defaultFocusColor};
    outline-offset: -2px;
    z-index: 1; /* Ensure focus outline appears above adjacent elements */
  }
`

/**
 * Focus-within indicator for container elements.
 * Shows focus ring when any child element is focused.
 *
 * Use for: form groups, checkbox/radio wrappers, switch components
 *
 * @param color - Focus ring color (default: baseColor for form elements)
 */
export const focusWithinIndicator = css`
  &:focus-within {
    outline: 2px solid ${baseColor};
    outline-offset: 2px;
    z-index: 1; /* Ensure focus outline appears above adjacent elements */
  }
`

/**
 * Focus indicator for tooltip-accessible elements (spans/divs with tabIndex={0}).
 * These elements need visible focus for keyboard users navigating to read tooltips.
 *
 * Use for: elements wrapped in Tooltip that have tabIndex={0}
 */
export const tooltipFocusIndicator = css`
  &:focus {
    outline: 2px solid ${baseColor};
    outline-offset: 1px;
    border-radius: 2px;
  }
`

/**
 * Focus-within indicator with background highlight for form control wrappers.
 * Includes smooth transition for better UX.
 *
 * Use for: Checkbox, Radio, Switch - elements where the native input is visually hidden
 *
 * @param backgroundColor - Background color on focus (default: use hoverColor from component)
 * @param offset - Outline offset (default: 2px, use 4px for Switch)
 */
export const focusWithinWithBackground = (backgroundColor: string, offset = '2px') => css`
  /* WCAG 2.4.7: Visible focus indicator for keyboard navigation */
  &:focus-within {
    background-color: ${backgroundColor};
    outline: 2px solid ${baseColor};
    outline-offset: ${offset};
    z-index: 1;
    transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
`

/**
 * Focus-visible indicator with background highlight and inset outline.
 * For directly focusable list items and menu items.
 *
 * Use for: CheckboxListItem, Menu items, dropdown options
 *
 * @param backgroundColor - Background color on focus
 */
export const focusVisibleWithBackground = (backgroundColor: string) => css`
  /* WCAG 2.4.7: Visible focus indicator for keyboard navigation */
  &:focus-visible {
    background-color: ${backgroundColor};
    cursor: pointer;
    outline: 2px solid ${baseColor};
    outline-offset: -2px;
    z-index: 1;
  }
`
